import { Button } from "@/components/ui/button.tsx"
import { ConnectMailboxButtons } from "@/connect_mailbox/connect_mailbox_buttons"
import { useAsyncEffect } from "@/utils"
import { DateTime } from "luxon"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { use_connect_redirect_to } from "../use_connect_redirect_to"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { SyncStatusDisplay } from "@/settings/integrations/sync-status-row"
import { PortalLoginSection } from "../portal_login_section"
import {
  findIntegrationByName,
  getIntegrationsWithJobs,
  IntegrationWithJobInfo,
  upsertIntegration,
  updateIntegration,
} from "@/api/integration"

const THRIVE_NAME = "thrive"
// The brand's Coupa Supplier Portal login lives on its own row: the mail sync above needs none.
const THRIVE_PORTAL_NAME = "thrive-portal"
const COUPA_PORTAL_URL = "https://supplier.coupahost.com"
// Thrive has no login of its own, so there is no meaningful username — a stable placeholder keeps
// the upsert idempotent, the same trick the BOL email sync and GFI use.
const STABLE_USERNAME = "default"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ConnectThrivePage(props: any = {}) {
  const redirectTo = use_connect_redirect_to(props.redirectTo)
  const [, setLocation] = useLocation()
  const currentPath = window.location.pathname

  const [emailIntegrations, setEmailIntegrations] = useState<IntegrationWithJobInfo[]>([])
  const [thriveIntegrationId, setThriveIntegrationId] = useState<string | null>(null)
  const [thriveLastSyncJob, setThriveLastSyncJob] = useState<IntegrationWithJobInfo["last_sync_job"]>(undefined)
  const [portal, setPortal] = useState<IntegrationWithJobInfo | null>(null)
  const [reloads, setReloads] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  useAsyncEffect(async () => {
    // with-jobs rather than the plain list: `/integration` returns bare rows with no
    // `last_sync_job`, so the sync status below would read "Not synced yet" forever.
    const res = await getIntegrationsWithJobs()
    if (!res.ok) {
      setLoadError("Could not load integrations")
      return
    }
    const all = res.value.data
    setEmailIntegrations(all.filter((i: IntegrationWithJobInfo) => i.name === "gmail" || i.name === "microsoft"))
    const thrive = findIntegrationByName(all, THRIVE_NAME)
    if (thrive) {
      setThriveIntegrationId(thrive.id)
      // The sync runs on Thrive's own integration id, not on a mailbox's, so this is the job that
      // says whether the connection is doing its work.
      setThriveLastSyncJob(thrive.last_sync_job)
    }
    setPortal(findIntegrationByName(all, THRIVE_PORTAL_NAME) ?? null)
  }, [reloads])

  async function handleSave() {
    setLoading(true)
    setLoadError(null)
    let id = thriveIntegrationId
    if (!id) {
      const created = await upsertIntegration(THRIVE_NAME, STABLE_USERNAME, {})
      if (!created.ok) {
        setLoadError("Could not enable the Thrive Market connection")
        setLoading(false)
        return
      }
      id = created.value.data.id
      setThriveIntegrationId(id)
    }
    const dataRes = await updateIntegration(id, {
      data: {},
      enabled: true,
    })
    if (!dataRes.ok) {
      setLoadError("Could not update settings")
      setLoading(false)
      return
    }
    setLoading(false)
    setLocation(redirectTo)
  }

  const has_mailbox = emailIntegrations.length > 0

  return (
    <CenterColumnContainer>
      <div class="space-y-6 max-w-md mx-auto">
        <h1 class="text-xl font-semibold text-gray-900 text-center">Sync Thrive Market deductions from email</h1>
        <p class="text-gray-500 text-sm text-center">
          Thrive Market has no deduction portal — credit memos arrive from their NetSuite through the day, and Coupa
          Pay mails remittance advices overnight. We read those from the mailboxes already connected below and turn each
          document into a deduction, on a daily weekday sync.
        </p>

        {thriveIntegrationId ? (
          <div class="border border-gray-200 rounded-lg p-4">
            <h3 class="font-medium text-gray-900 text-sm mb-1">Sync status</h3>
            <SyncStatusDisplay lastSyncJob={thriveLastSyncJob} integrationName={THRIVE_NAME} />
          </div>
        ) : null}

        <div class="space-y-3">
          <p class="text-sm font-medium text-gray-800">Connect email</p>
          <ConnectMailboxButtons
            gmailLabel="Add Gmail"
            microsoftLabel="Add Microsoft 365"
          />
        </div>

        {has_mailbox ? (
          <div class="border border-gray-200 rounded-lg p-4 space-y-2">
            <h3 class="font-medium text-gray-900 text-sm">Connected mailboxes</h3>
            {emailIntegrations.map((e: IntegrationWithJobInfo) => {
              const updated = e.session_updated_at
                ? DateTime.fromISO(e.session_updated_at).toLocaleString(DateTime.DATETIME_MED)
                : ""
              return (
                <div key={e.id} class="flex justify-between gap-4 text-sm">
                  <span class="text-gray-700 truncate">
                    {e.name}: {e.username}
                  </span>
                  {updated ? <span class="text-gray-400 shrink-0">{updated}</span> : null}
                </div>
              )
            })}
          </div>
        ) : (
          <p class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            No mailbox is connected yet. Thrive sends no portal feed, so this connection has nothing to read until the
            mailbox that receives their credit memo and remittance emails is connected above.
          </p>
        )}

        <section class="border border-gray-200 rounded-lg p-4 space-y-4">
          <h2 class="font-medium text-gray-900 text-sm">Coupa Supplier Portal login (optional)</h2>
          {portal ? <SyncStatusDisplay lastSyncJob={portal.last_sync_job} integrationName={THRIVE_PORTAL_NAME} /> : null}
          {/* Keyed so the form starts from the saved login once the integration has loaded. */}
          <PortalLoginSection
            key={portal?.id ?? "none"}
            integration_name={THRIVE_PORTAL_NAME}
            portal_label="Coupa Supplier Portal"
            username_label="Coupa email"
            username={portal?.username}
            onSaved={() => setReloads((n: number) => n + 1)}>
            <p class="text-sm text-gray-500">
              Thrive pays through Coupa. With the login you use at{" "}
              <a href={COUPA_PORTAL_URL} target="_blank" rel="noopener noreferrer" class="text-blue-700 hover:underline">
                supplier.coupahost.com
              </a>{" "}
              saved, we also read what settled each invoice every weekday — every credit Thrive applied and the payment
              it came off — so a credit Thrive spread across several invoices lands on the right payments, and a credit
              that never reached your inbox is still picked up. We only read: nothing is created or changed in Coupa.
            </p>
            <p class="text-sm text-gray-500">
              In Coupa, under Security &amp; MFA, keep Multi Factor Authentication set to "For Payment Changes" so
              signing in needs no code.
            </p>
          </PortalLoginSection>
        </section>

        {loadError ? <p class="text-sm text-red-600">{loadError}</p> : null}

        <div class="flex justify-between gap-3">
          <Button type="button" variant="outline" onClick={() => setLocation(redirectTo)}>
            Back
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={loading} class="bg-gray-900 text-white">
            {loading ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </CenterColumnContainer>
  )
}
